import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link } from 'react-router-dom';
import RegistrationForm from '../../features/event-form-feature/RegistrationForm';
import type { EventFormData } from '../../features/event-form-feature/EventFormContainer';
import { mapApiEventToFormData } from '../../features/event-form-feature/mapApiEventToFormData';
import {
  buildEventFormValues,
  eventFormFullSchema,
  finalizeEventFormDataForSubmit,
  getFirstEventFormError,
  REGISTRATION_SETTINGS_FIELDS,
} from '../../features/event-form-feature/eventFormShared';
import { eventService } from '../../services/eventService';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';

interface EventRegistrationQuickEditPanelProps {
  eventId: string;
  onSaved?: () => void;
}

const EventRegistrationQuickEditPanel: React.FC<EventRegistrationQuickEditPanelProps> = ({
  eventId,
  onSaved,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const t = useBrandRegistrationCopy();

  const methods = useForm<EventFormData>({
    defaultValues: buildEventFormValues(),
    resolver: yupResolver(eventFormFullSchema) as any,
    mode: 'onChange',
    shouldUnregister: false,
  });

  const loadEvent = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await eventService.getEventWorkspace(eventId);
      const mapped = mapApiEventToFormData(response.data);
      methods.reset(buildEventFormValues(mapped));
      setLoadedAt(new Date());
    } catch {
      setLoadError(t('Não foi possível carregar as configurações de inscrição.'));
    } finally {
      setLoading(false);
    }
  }, [eventId, methods, t]);

  useEffect(() => {
    void loadEvent();
  }, [loadEvent]);

  const handleSave = async () => {
    const isValid = await methods.trigger([...REGISTRATION_SETTINGS_FIELDS]);
    if (!isValid) {
      const firstError = getFirstEventFormError(methods.formState.errors);
      setFeedback({
        type: 'error',
        message: firstError || 'Corrija os erros antes de salvar.',
      });
      return;
    }

    setSaving(true);
    try {
      const payload = finalizeEventFormDataForSubmit(methods.getValues());
      await eventService.updateEvent(eventId, payload);
      setFeedback({ type: 'success', message: t('Configurações de inscrição salvas.') });
      setLoadedAt(new Date());
      onSaved?.();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Erro ao salvar configurações.';
      setFeedback({ type: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert severity="error" action={<Button onClick={() => void loadEvent()}>Tentar de novo</Button>}>
        {loadError}
      </Alert>
    );
  }

  return (
    <FormProvider {...methods}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t('Formulário de inscrição')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Prazo, campos personalizados e opções do formulário.
          </Typography>
          {loadedAt && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Carregado às {loadedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
        </Box>

        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
            <RegistrationForm embedded />
          </Box>

          <Divider />

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            spacing={1.5}
            justifyContent="flex-end"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ p: { xs: 1.5, md: 2 }, bgcolor: 'action.hover' }}
          >
            <Button
              component={Link}
              to={`/dashboard/eventos/${eventId}/editar?step=inscricao`}
              variant="outlined"
              startIcon={<OpenInNewIcon />}
            >
              Abrir no editor principal
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              disabled={saving}
              onClick={() => void handleSave()}
            >
              {t('Salvar inscrição')}
            </Button>
          </Stack>
        </Paper>
      </Stack>

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={5000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {feedback ? (
          <Alert severity={feedback.type} onClose={() => setFeedback(null)} sx={{ width: '100%' }}>
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </FormProvider>
  );
};

export default EventRegistrationQuickEditPanel;
